import { useMemo, useState } from 'react';
import { FaFileCsv } from 'react-icons/fa';
import { ExportToCsv } from 'export-to-csv';
import { useApiClient } from '../../hooks/useApiClient';
import PaginatedVehicleTable from '../../components/VehicleTable/PaginatedVehicleTable';
import { Column } from 'react-table';
import { columnsData } from '../../components/VehicleTable/columnsData';
import ErrorBoundary from '../../components/ErrorBoundary/ErrorBoundary';
import toaster from '../../utils/toaster';
import { Button, Col, Container, Row } from 'react-bootstrap';
import { GTMsendEvent } from '../../utils/trackingGA4';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'src/hooks/useAuth';
import { RoleEnum } from 'src/api';
import {
	StellantisRequestQuoteModal,
	StellantisVehicleEmptyState,
} from 'src/components/Stellantis';
import VehicleEmptyState from 'src/components/VehicleTable/components/VehicleEmptyState';
import { toServerColumns } from 'src/vehicleList/columns';
import { buildVehicleExportData } from 'src/vehicleList/exportData';
import { ExportProgress, fetchAllPages } from 'src/vehicleList/fetchAllPages';
import {
	availabilityOptions,
	roleScopeOf,
	stateOptions,
} from 'src/vehicleList/filterOptions';
import { usePaginatedVehicles } from 'src/vehicleList/usePaginatedVehicles';

const VehiclePromoListPage = () => {
	const { apiClient } = useApiClient();
	const { t } = useTranslation();
	const { userRole, isSalesStellantis, isSalesFacile, canViewPromoNote } =
		useAuth();
	const navigate = useNavigate();

	const [exportProgress, setExportProgress] = useState<ExportProgress | null>(
		null
	);
	const isExporting = exportProgress !== null;

	const scope = useMemo(
		() => roleScopeOf({ userRole, isSalesFacile, isSalesStellantis }),
		[userRole, isSalesFacile, isSalesStellantis]
	);

	const { items, loading, error, request, tableProps } = usePaginatedVehicles({
		cacheKey: 'promo',
		scope,
		baseParams: { promo: true, sortBy: 'disponibili', sortDirection: 'asc' },
	});

	const handleExportButton = async () => {
		setExportProgress({ loaded: 0, total: tableProps.totalCount });

		try {
			const all = await fetchAllPages(
				(params) =>
					apiClient.motornetControllerService.getPlatformVehicles(params),
				request,
				setExportProgress
			);

			const csvExporter = new ExportToCsv({
				filename: i18next.t('list_vehicles_promo'),
			});
			csvExporter.generateCsv(
				buildVehicleExportData(all, { canViewPromoNote })
			);

			GTMsendEvent({
				event: 'CTA_download_file',
				evtCat: 'lista_veicoli_promo',
				evtAct: 'download',
				evtLab: `${i18next.t('list_vehicles_promo')}.csv`,
			});
		} catch (err: any) {
			toaster.error(err?.message || i18next.t('error_loading_data'));
		} finally {
			setExportProgress(null);
		}
	};

	const handleDoubleClick = (row: any) => {
		navigate(`/details/${row.original.id}`);
	};

	const columns = useMemo(
		() =>
			toServerColumns(
				columnsData.filter((c) => {
					if (
						['display_reporter', 'massive_update_selector'].includes(c.accessor)
					) {
						return false;
					}

					if (c.id === 'promo_note') {
						return canViewPromoNote;
					}

					return true;
				})
			) as Column<Object>[],
		[canViewPromoNote]
	);

	const filterOptions = useMemo(
		() => ({
			stato_corrente: stateOptions(scope),
			stato_disp: availabilityOptions(),
		}),
		[scope]
	);

	const handleStellantisEmptyState = [
		RoleEnum.SEGNALATORE,
		RoleEnum.SALES,
		RoleEnum.SALES_DEALER,
	].includes(userRole);

	return (
		<Container fluid>
			<Row>
				<Col md={6} className="display-6">
					{t('list_vehicles_promo')}
				</Col>

				<Col md={6} className="mt-4 mt-md-0 text-md-end">
					<Button
						className="btn me-3"
						onClick={handleExportButton}
						disabled={loading || isExporting}
					>
						<FaFileCsv className="fs-5" />{' '}
						{exportProgress
							? `${exportProgress.loaded.toLocaleString(
									'it-IT'
							  )} / ${exportProgress.total.toLocaleString('it-IT')}`
							: t('download').toUpperCase()}
					</Button>
				</Col>
			</Row>

			<ErrorBoundary>
				<PaginatedVehicleTable
					{...tableProps}
					columns={columns}
					data={items}
					loading={loading}
					error={error}
					filterOptions={filterOptions}
					hiddenColumns={['id', 'prezzo_listino']}
					handleDoubleClick={handleDoubleClick}
					EmptyComponent={
						handleStellantisEmptyState
							? ({ filterState }) => {
									const specificFiltersApplied = [
										filterState?.marca,
										filterState?.modello,
										filterState?.allestimento,
									].some((value) => Boolean(value));

									return specificFiltersApplied ? (
										<StellantisVehicleEmptyState source="lista_promo" />
									) : (
										<VehicleEmptyState
											message={i18next.t('vehicle_empty_state_title')}
										/>
									);
							  }
							: () => (
									<VehicleEmptyState
										message={i18next.t('vehicle_empty_state_title')}
									/>
							  )
					}
				></PaginatedVehicleTable>
			</ErrorBoundary>
			{handleStellantisEmptyState ? <StellantisRequestQuoteModal /> : null}
		</Container>
	);
};

export default VehiclePromoListPage;
